import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { Visibility, Edit, AutoAwesome } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

interface MarkdownEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onAiSuggestion?: () => Promise<void>;
  loading?: boolean;
  placeholder?: string;
  helperText?: string;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  label,
  value,
  onChange,
  onBlur,
  onAiSuggestion,
  loading = false,
  placeholder = '',
  helperText = ''
}) => {
  const [editMode, setEditMode] = useState<'edit' | 'preview'>('preview');

  const toggleEditMode = () => {
    setEditMode(prev => (prev === 'edit' ? 'preview' : 'edit'));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {label}
        </Typography>
        <Box>
          {onAiSuggestion && (
            <Tooltip title="Get AI suggestion">
              <span>
                <IconButton
                  onClick={onAiSuggestion}
                  size="small"
                  sx={{ mr: 1 }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={22} /> : <AutoAwesome fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Button
            size="small"
            startIcon={editMode === 'edit' ? <Visibility /> : <Edit />}
            onClick={toggleEditMode}
            sx={{ textTransform: 'none' }}
          >
            {editMode === 'edit' ? 'Preview' : 'Edit'}
          </Button>
        </Box>
      </Box>

      {editMode === 'edit' ? (
        <TextField
          fullWidth
          multiline
          minRows={6}
          maxRows={16}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          sx={{
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              lineHeight: 1.6,
              fontSize: '0.95rem',
            },
          }}
        />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            minHeight: '150px',
            bgcolor: 'background.paper',
            borderRadius: 1,
            borderColor: 'divider',
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              mt: 1.5,
              mb: 1,
            },
            '& p': {
              mb: 1.5,
              '&:last-child': {
                mb: 0,
              },
            },
            '& ul, & ol': {
              pl: 3,
              mb: 1.5,
              '& li': {
                mb: 0.5,
              },
            },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'divider',
              pl: 2,
              ml: 0,
              color: 'text.secondary',
              fontStyle: 'italic',
            },
            '& code': {
              bgcolor: 'action.hover',
              px: 0.5,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.9em',
            },
            '& pre': {
              bgcolor: 'action.hover',
              p: 1.5,
              borderRadius: 1,
              overflowX: 'auto',
              '& code': {
                bgcolor: 'transparent',
                p: 0,
              },
            },
          }}
        >
          {value ? (
            <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
              {value}
            </ReactMarkdown>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No content to preview
            </Typography>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Supports markdown formatting
        </Typography>
      </Box>
    </Box>
  );
};

export default MarkdownEditor;
